import { useEffect, useState } from "react"
import { Plus, DollarSign, Calendar, FileText, Sparkles } from "lucide-react"
import AddExpenseModal from "./AddExpenseModal"
import { useDispatch, useSelector } from "react-redux"
import { allExpensesData } from "../../store/actions/userAction"

export default function Expenses() {
  const allexpensesdata = useSelector((state: any) => state.UserReducer.allexpensesdata || [])
  const userdata = useSelector((state: any) => state.UserReducer.userdata)

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [expensesList, setExpensesList] = useState(allexpensesdata.data || [])
  const [page, setPage] = useState(1)

  const stats = allexpensesdata?.stats || { totalAmount: 0, count: 0, currentMonthAmount: 0 }
  const pagination = allexpensesdata?.pagination || {}
  const { totalPages } = pagination

  const dispatch = useDispatch()

  const { store } = userdata
  const storeId = store?._id
  const limit = 6

  useEffect(() => {
    if (!storeId) return
    dispatch(allExpensesData(storeId, page, limit))
  }, [page, storeId, dispatch])

  useEffect(() => {
    setExpensesList(allexpensesdata?.data || [])
  }, [allexpensesdata])

  return (
    <>
      <div className="space-y-8">
        <section className="relative overflow-hidden rounded-3xl border border-slate-200 bg-gradient-to-br from-sky-600 via-blue-700 to-blue-900 p-6 sm:p-7 text-white shadow-xl">
          <div className="pointer-events-none absolute -top-14 -right-10 h-36 w-36 rounded-full bg-cyan-300/25 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-12 -left-8 h-32 w-32 rounded-full bg-blue-200/20 blur-3xl" />

          <div className="relative flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="inline-flex items-center gap-1 rounded-full bg-white/20 px-2.5 py-1 text-[11px] uppercase tracking-[0.16em]">
                <Sparkles size={12} /> Expense Tracker
              </p>
              <h1 className="mt-3 text-2xl sm:text-3xl font-bold">Dépenses</h1>
              <p className="mt-2 text-sm text-white/85 max-w-2xl">
                Suivi des charges de l'entreprise et visibilité claire sur vos sorties de caisse.
              </p>
            </div>

            <button
              onClick={() => setIsModalOpen(true)}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-white px-4 py-2 text-blue-700 hover:bg-blue-50 transition font-semibold"
            >
              <Plus size={18} />
              Ajouter une dépense
            </button>
          </div>
        </section>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Kpi
            label="Total des dépenses"
            value={`${Number(stats?.totalAmount || 0).toLocaleString()} FCFA`}
            icon={<DollarSign size={18} />}
            color="from-rose-600 to-red-500"
            ring="ring-rose-100"
          />
          <Kpi
            label="Nombre de dépenses"
            value={Number(stats?.count || 0)}
            icon={<FileText size={18} />}
            color="from-blue-600 to-cyan-600"
            ring="ring-blue-100"
          />
          <Kpi
            label="Ce mois-ci"
            value={`${Number(stats?.currentMonthAmount || 0).toLocaleString()} FCFA`}
            icon={<Calendar size={18} />}
            color="from-orange-600 to-amber-500"
            ring="ring-orange-100"
          />
        </div>

        <div className="hidden md:block overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-100 bg-gradient-to-r from-slate-50 to-white px-6 py-4">
            <p className="text-sm font-semibold text-slate-800">Historique des dépenses</p>
            <p className="text-xs text-slate-500 mt-0.5">Analysez vos dépenses par type et période.</p>
          </div>

          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr className="text-left text-slate-500">
                <th className="px-6 py-3">Date</th>
                <th className="px-6 py-3">Type</th>
                <th className="px-6 py-3">Description</th>
                <th className="px-6 py-3">Montant</th>
              </tr>
            </thead>

            <tbody>
              {expensesList?.map((e: any) => (
                <tr key={e.id || e._id} className="border-t border-slate-100 hover:bg-slate-50/70 transition">
                  <td className="px-6 py-4 text-slate-600">{e.expenseDate || e.date || "-"}</td>

                  <td className="px-6 py-4">
                    <span className="inline-flex rounded-full bg-blue-50 px-2.5 py-1 text-xs font-semibold text-blue-700">
                      {e.type}
                    </span>
                  </td>

                  <td className="px-6 py-4 text-slate-600">{e.description || "-"}</td>

                  <td className="px-6 py-4">
                    <span className="inline-flex rounded-full bg-rose-50 px-2.5 py-1 text-xs font-semibold text-rose-700">
                      {Number(e.amount || 0).toLocaleString()} FCFA
                    </span>
                  </td>
                </tr>
              ))}

              {expensesList?.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-6 py-10 text-center text-slate-500">
                    Aucune dépense enregistrée
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="space-y-4 md:hidden">
          {expensesList?.map((e: any) => (
            <div key={e.id || e._id} className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
              <div className="bg-gradient-to-r from-slate-50 to-blue-50 px-4 py-3 flex justify-between items-start">
                <div>
                  <p className="font-semibold text-slate-800">{e.type}</p>
                  <p className="text-xs text-slate-500">{e.expenseDate || e.date || "-"}</p>
                </div>
                <span className="text-xs bg-rose-100 text-rose-700 px-2 py-1 rounded-full">
                  {Number(e.amount || 0).toLocaleString()} FCFA
                </span>
              </div>

              {e.description && <p className="p-4 text-sm text-slate-600">{e.description}</p>}
            </div>
          ))}

          {expensesList?.length === 0 && <div className="text-center text-slate-500 py-10">Aucune dépense enregistrée</div>}
        </div>

        {totalPages > 1 && (
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-2">
            <p className="text-sm text-slate-500">
              Page <strong className="text-slate-700">{page}</strong> sur <strong className="text-slate-700">{totalPages}</strong>
            </p>

            <div className="flex gap-2">
              <button
                disabled={page === 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="px-4 py-2 rounded-xl border border-slate-200 disabled:opacity-50 hover:bg-slate-100"
              >
                Précédent
              </button>

              <button
                disabled={page === totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                className="px-4 py-2 rounded-xl bg-blue-600 text-white disabled:opacity-50 hover:bg-blue-700"
              >
                Suivant
              </button>
            </div>
          </div>
        )}
      </div>

      <AddExpenseModal
        storeId={store?._id}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onAdd={(newExpense) => {
          dispatch(allExpensesData(storeId, page, limit))
          setExpensesList((prev: any) => [newExpense, ...prev])
        }}
      />
    </>
  )
}

function Kpi({
  label,
  value,
  icon,
  color,
  ring,
}: {
  label: string
  value: string | number
  icon: React.ReactNode
  color: string
  ring: string
}) {
  return (
    <div className={`rounded-2xl border border-slate-200 bg-white p-5 shadow-sm ring-1 ${ring}`}>
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-sm text-slate-500">{label}</p>
          <p className="text-2xl font-bold text-slate-800 mt-0.5">{value}</p>
        </div>
        <div className={`h-12 w-12 rounded-xl bg-gradient-to-br ${color} text-white flex items-center justify-center shadow-lg`}>
          {icon}
        </div>
      </div>
    </div>
  )
}
